import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

const SfmaHackathon = () => {
  const { t, i18n } = useTranslation();
  const words_ar = ["أبدع", "نافس", "ابتكر"];
  const words_en = ["Create", "Compete", "Innovate"];

  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [displayedWord, setDisplayedWord] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed] = useState(200); // Typing speed in ms

  const subjects = [
    t("hackathon.subjects.1"),
    t("hackathon.subjects.2"),
    t("hackathon.subjects.3"),
    t("hackathon.subjects.4"),
    t("hackathon.subjects.5"),
    t("hackathon.subjects.6"),
    t("hackathon.subjects.7"),
    t("hackathon.subjects.8"),
    t("hackathon.subjects.9"),
    t("hackathon.subjects.10"),
  ];
  const goals = [
    {
      id: 1,
      title: t("hackathon.goals.1.title"),
      icon: "/images/icon-06.svg",
      width: "200px",
    },
    {
      id: 2,
      title: t("hackathon.goals.2.title"),
      icon: "/images/icon-05.svg",
      width: "200px",
    },
    {
      id: 3,
      title: t("hackathon.goals.3.title"),
      icon: "/images/icon-03.svg",
      width: "200px",
    },
    {
      id: 4,
      title: t("hackathon.goals.4.title"),
      icon: "/images/icon-01.svg",
      width: "230px",
    },
    {
      id: 5,
      title: t("hackathon.goals.5.title"),
      icon: "/images/icon-04.svg",
      width: "200px",
    },
  ];

  useEffect(() => {
    const handleTyping = () => {
      const currentWords = i18n.language === "en" ? words_en : words_ar;
      const currentWord = currentWords[currentWordIndex];

      if (!isDeleting) {
        // Typing the word
        setDisplayedWord((prev) => currentWord.slice(0, prev.length + 1));
        if (displayedWord === currentWord) {
          setTimeout(() => setIsDeleting(true), 1000); // Pause before deleting
        }
      } else {
        // Deleting the word
        setDisplayedWord((prev) => prev.slice(0, -1));
        if (displayedWord === "") {
          setIsDeleting(false);
          setCurrentWordIndex(
            (prevIndex) => (prevIndex + 1) % currentWords.length
          ); // Move to the next word
        }
      }
    };

    const typingTimer = setTimeout(handleTyping, isDeleting ? 50 : typingSpeed);

    return () => clearTimeout(typingTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- words_ar/words_en static
  }, [displayedWord, isDeleting, currentWordIndex, i18n.language, typingSpeed]);

  return (
    <section
      className={`min-h-[calc(100vh - 64px)] grid grid-cols-12 ${
        i18n.language === "en" ? "text-left" : "text-right"
      }`}
      dir={i18n.language === "en" ? "ltr" : "rtl"}
    >
      <Helmet>
        <title>{t("hackathon.helmet.title")}</title>
      </Helmet>
      <div className="lg:col-span-12 col-span-12 back-ground_2 h-[65vh]">
        <img
          src="/images/page_bg.png"
          className="about-img col-span-12 h-[500px]"
          alt={t("hackathon.about.imageAlt")}
        />
        <div
          className={`text text-center container mx-auto  ${
            i18n.language === "en" ? "md:text-left" : "md:text-right"
          }`}
        >
          <img
            src="/images/شعار-سفما-هاكثون-w.png"
            className="2xl:h-56 h-40 2xl:mt-20 md:mt-10 mt-4 2xl:mb-6 md:mb-3 mx-auto"
            alt="Logo"
          />
          <div className="md:flex justify-between translate-y-2">
            <h3 className="md:text-[50px] text-[24px] font-bold leading-relaxed">
              {t("hackathon.banner.launch")}{" "}
              <span className="md:inline block">
                {" "}
                <span className="text-[#35E8EE]">{displayedWord}</span>
                <span className="cursor">|</span>
              </span>
            </h3>
            <a
              href="https://sfma.sa"
              className="md:text-[20px] text-lg font-medium leading-relaxed text-white md:translate-y-16 translate-y-5 md:inline inline-block hover:text-main md:opacity-90"
              target="_blank"
              rel="noreferrer"
            >
              {t("hackathon.banner.poweredBy")}
            </a>
          </div>
        </div>
      </div>

      <div
        className="col-span-12 md:pt-16 py-10"
        style={{
          backgroundImage: `url(/images/bg.jpg)`,
          backgroundRepeat: "repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
        }}
      >
        <div className="lg:grid md:grid grid-cols-12 gap-x-28 container">
          <div className="lg:col-span-6 col-span-12">
            <p className="lg:text-3xl text-lg font-bold text-white bg-[#e65403] mb-2 py-2 px-8 w-fit lg:mx-0 mx-auto">
              {t("hackathon.goal.title")}
            </p>
            <p
              className="lg:text-2xl lg:text-justify text-center font-semibold text-gray-500 mt-4"
              style={{ lineHeight: "40px" }}
            >
              {t("hackathon.goal.description")}
            </p>
            <div className="flex justify-center items-center gap-10">{}</div>
          </div>
          <div className="md:col-span-6 col-span-12 my-auto md:mt-0 mt-9 h-full">
            <img src="/images/hackathon.png" className="lg:h-full" alt="Logo" />
          </div>
        </div>
      </div>

      <div
        className="col-span-12 text-center lg:py-10 py-0"
        style={{
          backgroundImage: `url(/images/bg.jpg)`,
          backgroundRepeat: "repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
        }}
      >
        <div className="container">
          <p className="text-xl text-[#e65403] font-bold lg:text-3xl">
            {t("hackathon.subjects.title")}
          </p>
          <p className="text-gray-700 mt-3 mb-6 font-semibold lg:text-xl">
            {t("hackathon.subjects.description")}
          </p>
          <div className="flex flex-wrap justify-between items-center w-full gap-6 px-2 mb-10">
            {subjects.map((item, idx) => {
              return (
                <p
                  className="bg-[#00b5ad] p-2 text-white lg:max-w-[32%] lg:min-w-[32%] w-full lg:text-[23px] lg:min-h-[100px] min-h-[80px] flex items-center justify-center px-6 font-semibold rounded-lg mx-auto"
                  key={idx}
                >
                  {item}
                </p>
              );
            })}
          </div>
        </div>
      </div>

      <div className="col-span-12 text-center back-ground_2 lg:max-h-screen">
        <img
          src="/images/section_bg.png"
          className="about-img col-span-12 h-[500px]"
          alt={t("hackathon.about.imageAlt")}
        />
        <div className="absolute 2xl:top-1/3 top-0 left-0 w-full h-full z-50 2xl:-translate-y-1/3">
          <img
            src="/images/شعار-سفما-هاكثون-w.png"
            className="2xl:h-36 md:h-28 h-16 2xl:mt-14 md:mt-6 mt-4 2xl:mb-6 md:mb-3 mx-auto"
            alt="Logo"
          />
          <p className="2xl:text-3xl md:text-xl text-[9px] font-bold text-white bg-[#e65403] mb-3 md:py-3 py-[6px] px-5 md:px-10 w-fit mx-auto">
            {t("hackathon.target.title")}
          </p>
          <div className="flex lg:flex-wrap lg:justify-between justify-around items-start w-full lg:gap-x-6 gap-x-2 lg:gap-y-6 gap-y-2 px-2 mb-10 container 2xl:mt-16 md:mt-12 mt-3">
            {goals.map((item, idx) => {
              return (
                <div>
                  <div
                    key={item.id}
                    className="rounded-full lg:pt-6 pt-2 bg-[#00b5ad] 2xl:min-h-[220px] md:min-h-[190px] min-h-[75px] flex flex-col justify-between w-fit mx-auto"
                  >
                    <p className="text-white 2xl:text-6xl md:text-3xl text-sm font-bold lg:px-8 px-4">
                      {item.id}
                    </p>
                    <div
                      className="rounded-full lg:border-4 border-white bg-gray-200 w-full 2xl:h-[100px] md:h-[90px] h-[40px] mt-auto flex justify-center items-center"
                      style={{
                        boxShadow: "inset 0 4px 6px rgba(0, 0, 0, 0.1)",
                        position: "relative",
                      }}
                    >
                      <img
                        src={item.icon}
                        className="2xl:w-10 md:w-8 w-5 h-auto"
                        alt="Logo"
                        style={{
                          position: "relative",
                          zIndex: 1,
                        }}
                      />
                    </div>
                  </div>
                  <p className="text-white mt-2 2xl:max-w-[200px] md:max-w-[160px] max-w-[60px] min-h-[100px] 2xl:font-bold lg:font-semibold 2xl:text-2xl md:text-[21px] text-[9px]">
                    {item.title}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="col-span-12 lg:mt-12 mt-8 flex justify-center gap-x-8 container">
        <a
          href={
            i18n.language === "en"
              ? `/images/SFMA_Hackathon_English.pdf`
              : `/images/SFMA_HACKATHON.pdf`
          }
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-center text-center lg:min-h-[60px] min-h-[32px] bg-[#00b5ad] text-white lg:max-w-[32%] lg:min-w-[32%] w-full lg:text-[23px] text-xs px-6 font-bold rounded-lg"
        >
          {t("hackathon.details")}
        </a>

        <a
          href="https://hackathon.sic.org.sa/"
          target="_blank"
          rel="noreferrer"
          className=" flex items-center justify-center text-center lg:min-h-[60px] min-h-[32px] bg-[#00b5ad] text-white lg:max-w-[32%] lg:min-w-[32%] w-full lg:text-[23px] text-xs px-6 font-bold rounded-lg"
        >
          {t("hackathon.register")}
        </a>
      </div>
      <div className="col-span-12 flex justify-center items-center lg:my-4 my-2 ">
        <img
          src="/images/تحول.png"
          className="2xl:h-56 lg:h-40 h-20 mx-auto"
          alt="Logo"
        />
        <img
          src="/images/نادي-الابتكار.png"
          className="2xl:h-56 lg:h-40 h-20 mx-auto"
          alt="Logo"
        />
        <img
          src="/images/الجمعية.png"
          className="2xl:h-56 lg:h-40 h-20 mx-auto"
          alt="Logo"
        />
      </div>
    </section>
  );
};

export default SfmaHackathon;
